import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/useLanguage";
import { FileInput } from "../common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../images/ImageList";
import RadioButton from "../common/RadioButton";
import { FilterAdminstration } from "../../types/requestsOutputs";
import { FilterUpdatableFields, updateFilter } from "../../api/administration";
import { Tooltip } from "@mui/material";
import { useNotification } from "../../context/NotificationContext";
import { IMAGE_FORMAT } from "../../utils/image";
import formStyles from "../../pages/administration/CategoriesForm.module.css";
import stepStyles from "../../pages/track/create/createStep.module.css";
import { ModalPortal } from "./ModalPortal";
import styles from "./CompanyEditModal.module.css";

interface FilterEditModalProps {
    filter: FilterAdminstration;
    isOpen: boolean;
    onClose: () => void;
}

const FilterEditModal: React.FC<FilterEditModalProps> = ({ filter, isOpen, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [activeTab, setActiveTab] = useState<"tracks" | "cities" | "info">("info");
    const { language } = useLanguage();
    const [selectedFilter, setSelectedFilter] = useState<FilterAdminstration>(filter);
    const [pictures, setPictures] = useState<string[]>([]);
    const [hasOnlyGardenStories, setHasOnlyGardenStories] = useState<boolean>(false);
    const [isDrivingMaps, setIsDrivingMaps] = useState<boolean>(false);
    const [originalLogo, setOriginalLogo] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const { showNotification } = useNotification();

    useEffect(() => {
        if (isOpen) {
            setSelectedFilter(filter); // reset à chaque ouverture
            const initialLogo = filter.logoUrl || null;
            setPictures(initialLogo ? [initialLogo] : []);
            setOriginalLogo(initialLogo);
            setHasOnlyGardenStories(filter.hasOnlyGardenStories || false);
            setIsDrivingMaps(filter.isDrivingMaps || false);
        }
    }, [isOpen, filter, language]);

    useEffect(() => {
        const changed =
            pictures[0] !== originalLogo ||
            hasOnlyGardenStories !== filter.hasOnlyGardenStories ||
            isDrivingMaps !== filter.isDrivingMaps;
        setHasChanges(changed);
    }, [pictures, hasOnlyGardenStories, isDrivingMaps, originalLogo, filter]);

    if (!isOpen) return null;

    const handleFileSelected = (file: File | File[]) => {
        if (Array.isArray(file)) {
            console.error("Multiple files not supported");
            return;
        }
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }

        uploadFileToS3Complete(
            {
                file,
                isPublic: true, // Public files for general uploads
                isFolder: false, // No folder structure for general uploads
            },
            token,
        )
            .then((result: string) => {
                setPictures([result]);
            })
            .catch((error) => console.error("Error uploading image:", error));
    };

    const handleUpdate = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }

        const updates: FilterUpdatableFields = {};

        if (pictures[0] !== originalLogo) {
            updates.logoUrl = pictures[0];
        }
        if (hasOnlyGardenStories !== filter.hasOnlyGardenStories) {
            updates.hasOnlyGardenStories = hasOnlyGardenStories;
        }
        if (isDrivingMaps !== filter.isDrivingMaps) {
            updates.isDrivingMaps = isDrivingMaps;
        }

        if (Object.keys(updates).length === 0) {
            console.log("No changes to update.");
            return;
        }

        console.log("Updating filter:", filter.id, updates);

        try {
            await updateFilter(token, filter.id, updates);
            showNotification({ message: t("categoryFilterSaved"), type: "success" });
            onClose();
        } catch (err) {
            console.error("Update failed:", err);
            showNotification({ message: t("updateFailed"), type: "error" });
        }
    };

    const isLogoMissing = !pictures[0];
    const disableUpdate = !hasChanges || isLogoMissing;

    return (
        <ModalPortal>
            <div className={styles.overlay}>
                <div className={styles.content}>
                    <h2>
                        {t("editFilter")} - {selectedFilter.internalName}
                    </h2>

                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab}${activeTab === "info" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("info")}
                        >
                            {t("info")}
                        </button>
                    </div>

                    {activeTab === "info" && (
                        <div className={formStyles.root}>
                            <h4>{t("logoUrl")}</h4>
                            <div className={stepStyles.fileUpload}>
                                <label>{t("insertYourLogo")}</label>
                                <FileInput onFileSelected={handleFileSelected} acceptedFileTypes={IMAGE_FORMAT} />
                            </div>

                            {pictures && pictures.length > 0 && (
                                <div className="photo-gallery">
                                    <ImageList pictures={pictures} setPictures={setPictures} />
                                </div>
                            )}

                            <div className={stepStyles.stepDescription}>
                                <h2>{t("hasOnlyGardenStories")}</h2>
                                <div className={formStyles.radios}>
                                    <RadioButton
                                        label={t("hasOnlyGardenStoriesYesTitle")}
                                        subtitle={t("hasOnlyGardenStoriesYesSub")}
                                        checked={hasOnlyGardenStories}
                                        onChange={() => setHasOnlyGardenStories(true)}
                                    />
                                    <RadioButton
                                        label={t("hasOnlyGardenStoriesNoTitle")}
                                        subtitle={t("hasOnlyGardenStoriesNoSub")}
                                        checked={!hasOnlyGardenStories}
                                        onChange={() => setHasOnlyGardenStories(false)}
                                    />
                                </div>
                            </div>

                            <div className={stepStyles.stepDescription}>
                                <h2>{t("isDrivingMaps")}</h2>
                                <div className={formStyles.radios}>
                                    <RadioButton
                                        label={t("isDrivingMapsYesTitle")}
                                        subtitle={t("isDrivingMapsYesSub")}
                                        checked={isDrivingMaps}
                                        onChange={() => setIsDrivingMaps(true)}
                                    />
                                    <RadioButton
                                        label={t("isDrivingMapsNoTitle")}
                                        subtitle={t("isDrivingMapsNoSub")}
                                        checked={!isDrivingMaps}
                                        onChange={() => setIsDrivingMaps(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.footer}>
                        <Tooltip
                            title={!hasChanges ? t("noChangesToUpdate") : isLogoMissing ? t("logoRequiredTooltip") : ""}
                            arrow
                        >
                            <span>
                                <button className={styles.buttonChange} onClick={handleUpdate} disabled={disableUpdate}>
                                    {t("update")}
                                </button>
                            </span>
                        </Tooltip>
                        <button className={styles.buttonCancel} onClick={onClose}>
                            {t("close")}
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default FilterEditModal;
